//		{/* EDIT PROFILE BUTTON is MUTED*/}



import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential, updatePassword, verifyBeforeUpdateEmail } from 'firebase/auth';
import { toast, ToastContainer } from 'react-toastify';
import { collection, getDoc, getDocs, query, where, doc, addDoc, setDoc, deleteDoc, updateDoc, arrayUnion, orderBy, writeBatch, limit, startAfter, runTransaction, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebaseConfig'; 
import './MyAccount.css'; // optional CSS file for styles
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { storage, db, auth } from '../firebaseConfig';
import Cropper from 'react-easy-crop';
import { clearReminderPending, cancelSubscription, logPayment, sendContactMessage } from '../firebase/firestoreHelpers';
import AdminDashboard from './AdminDashboard';
import ProfilePage from './ProfilePage';
import PageInfoButton from '../components/PageInfoButton';
import { getStatesWithHomeFirst } from '../utils/states';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const normalizeAgencyName = (value) =>
  String(value || '')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const sanitizeAgencyName = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const buildAgencyLookup = (agencies = []) => {
  const rawAgencies = [...new Set(agencies.filter(Boolean))]
    .map((agencyName) => sanitizeAgencyName(agencyName))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  const sortedAgencies = [];
  const normalizedMap = new Map();
  const searchEntries = [];

  rawAgencies.forEach((agencyName) => {
    const normalized = normalizeAgencyName(agencyName);
    if (!normalized || normalizedMap.has(normalized)) return;
    normalizedMap.set(normalized, agencyName);
    sortedAgencies.push(agencyName);
    searchEntries.push({ normalized, agencyName });
  });

  return { sortedAgencies, normalizedMap, searchEntries };
};

const buildSortedUniqueOptions = (items = []) =>
  [...new Set(items.map((item) => String(item || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

const workEmailBlocklist = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'msn.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'protonmail.ch',
  'pm.me',
  'gmx.com',
  'yandex.com',
  'mail.com',
  'zoho.com',
  'fastmail.com',
  'hey.com',
  'qq.com',
  'att.net',
  'comcast.net',
  'verizon.net',
];

const normalizeEmailValue = (value) =>
  String(value || '').trim().toLowerCase();

const buildRequestId = () => {
  if (
    typeof window !== 'undefined' &&
    typeof window.crypto?.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }
  return `email-change-${Date.now()}`;
};

const getEmailChangeContinueUrl = () => {
  if (
    typeof window !== 'undefined' &&
    /^https?:\/\//.test(window.location.origin || '')
  ) {
    return `${window.location.origin}/confirm-new-email`;
  }
  return 'https://www.blue-wire.org/confirm-new-email';
};

const getEditableSelectionState = (value, options = []) => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return { selectedValue: '', customValue: '' };
  }

  if (options.includes(trimmedValue)) {
    return { selectedValue: trimmedValue, customValue: '' };
  }

  return { selectedValue: 'Other', customValue: trimmedValue };
};

const PROFILE_EDIT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

const timestampToDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

async function upsertRegistrationOption(field, item) {
  const value = String(item || '').trim();
  if (!value) return;

  const ref = doc(firestore, 'config', 'registrationOptions');

  await runTransaction(firestore, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const current = Array.isArray(data[field]) ? data[field] : [];
    const exists = current.some(
      (entry) => String(entry || '').trim().toLowerCase() === value.toLowerCase()
    );

    if (exists) return;

    const updated = [...current, value].sort((a, b) => a.localeCompare(b));

    if (snap.exists()) {
      tx.update(ref, { [field]: updated });
    } else {
      tx.set(ref, { [field]: updated });
    }
  });
}

async function upsertProfileSearchLookupsByState(stateName, agencyName, rankName, assignmentName) {
  const trimmedState = sanitizeAgencyName(stateName);
  const trimmedAgencyName = sanitizeAgencyName(agencyName);
  const trimmedRankName = String(rankName || '').trim();
  const trimmedAssignmentName = String(assignmentName || '').trim();
  if (!trimmedState) return;

  const writes = [];
  if (trimmedAgencyName) {
    writes.push(setDoc(
      doc(firestore, 'agenciesByState', trimmedState),
      { agencies: arrayUnion(trimmedAgencyName) },
      { merge: true }
    ));
  }

  const profileLookupPayload = { updatedAt: serverTimestamp() };
  if (trimmedRankName) {
    profileLookupPayload.ranks = arrayUnion(trimmedRankName);
  }
  if (trimmedAssignmentName) {
    profileLookupPayload.assignments = arrayUnion(trimmedAssignmentName);
  }

  if (profileLookupPayload.ranks || profileLookupPayload.assignments) {
    writes.push(setDoc(
      doc(firestore, 'profileSearchLookupsByState', trimmedState),
      profileLookupPayload,
      { merge: true }
    ));
  }

  await Promise.all(writes);
}





const MyAccount = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [currentUser, setCurrentUser] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [rank, setRank] = useState('');
  const [assignment, setAssignment] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [password, setPassword] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newAgency, setNewAgency] = useState(currentUser?.agency || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRankAssignment, setIsEditingRankAssignment] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isEditingAgency, setIsEditingAgency] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [showDeleteAccountForm, setShowDeleteAccountForm] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [pendingNewEmail, setPendingNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [showEmailChangeConfirmModal, setShowEmailChangeConfirmModal] = useState(false);
  const [timerRemaining, setTimerRemaining] = useState('');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const [referrals, setReferrals] = useState({ all: [], used: [] });
  const [activityLog, setActivityLog] = useState([]);
  const [visibleLogCount, setVisibleLogCount] = useState(4);
  const [contactTitle, setContactTitle] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [userMessages, setUserMessages] = useState([]);
  const [newState, setNewState] = useState(currentUser?.state || "");
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [agencyLookup, setAgencyLookup] = useState(() => new Map());
  const [agencySearchEntries, setAgencySearchEntries] = useState([]);
  const [rankOptions, setRankOptions] = useState([]);
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [agencySelection, setAgencySelection] = useState('');
  const [customAgency, setCustomAgency] = useState('');
  const [rankSelection, setRankSelection] = useState('');
  const [customRank, setCustomRank] = useState('');
  const [assignmentSelection, setAssignmentSelection] = useState('');
  const [customAssignment, setCustomAssignment] = useState('');
  const [biometricEnabled, setBiometricEnabled] = useState(false);


 
	
	const passwordFormRef = useRef(null);
  const deleteAccountFormRef = useRef(null);
		{/* Loading User Profile*/}

	useEffect(() => {
  const unsubscribe = onAuthStateChanged(getAuth(), async (authUser) => {
    if (authUser) {
      try {
        const tokenResult = await authUser.getIdTokenResult();
        setIsPlatformAdmin(tokenResult.claims?.admin === true);
        const userRef = doc(firestore, 'users', authUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setCurrentUser({ ...data, uid: authUser.uid });
	  setSubscription(data.subscription || null);
          setFirstName(data.firstName || '');
          setMiddleInitial(data.middleInitial || '');
          setLastName(data.lastName || '');
          setRank(data.rank || '');
          setAssignment(data.assignment || '');
          setAboutMe(data.aboutMe || '');
	  setActivityLog(data.activityLog || []);

	   setReferrals({
   		 all: data.referrals || [],
   		 used: data.usedReferrals || [],
  		});

          // 🔷 Resume timer if active
          if (
            data.notificationPrefs?.timer &&
            data.notificationPrefs?.timerExpiresAt
          ) {
            const expiresAt = new Date(
              data.notificationPrefs.timerExpiresAt
            ).getTime();
            const msUntilToggle = expiresAt - Date.now();

            if (msUntilToggle > 0) {
              scheduleTimer(msUntilToggle);
	      startCountdown(data.notificationPrefs.timerExpiresAt);
            }
          }
        } else {
          console.warn('User profile not found in Firestore.');
        }
      } catch (error) {
        setIsPlatformAdmin(false);
        console.error('Error fetching user profile:', error);
      }
    } else {
      setIsPlatformAdmin(false);
      console.warn('No authenticated user found.');
    }

      setLoadingUser(false);
    });
  
    return () => unsubscribe();
  }, []);

  // Load biometric preference from device storage (native only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const loadBiometricPref = async () => {
      try {
        const { value } = await Preferences.get({ key: 'biometricEnabled' });
        setBiometricEnabled(value === 'true');
      } catch (err) {
        console.warn('Failed to load biometric preference:', err);
      }
    };

    loadBiometricPref();
  }, []);
  
  
  
      useEffect(() => {
  if (currentUser) {
    setNewAgency(currentUser.agency || "");
  }
}, [currentUser]);



  useEffect(() => {
  if (currentUser) {
    setNewState(currentUser.state || "");
  }
}, [currentUser]);

  useEffect(() => {
    let cancelled = false;

    const loadRegistrationOptions = async () => {
      try {
        const snap = await getDoc(doc(firestore, 'config', 'registrationOptions'));
        if (!snap.exists()) return;

        const data = snap.data();
        if (cancelled) return;

        setRankOptions(buildSortedUniqueOptions(Array.isArray(data.ranks) ? data.ranks : []));
        setAssignmentOptions(
          buildSortedUniqueOptions(Array.isArray(data.assignments) ? data.assignments : [])
        );
      } catch (err) {
        console.error('Failed to load profile edit options:', err);
      }
    };

    loadRegistrationOptions();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStateAgencies = async () => {
      if (!newState) {
        setAgencyOptions([]);
        setAgencyLookup(new Map());
        setAgencySearchEntries([]);
        return;
      }

      try {
        const stateSnap = await getDoc(doc(firestore, 'agenciesByState', newState));
        const agencies = stateSnap.exists() && Array.isArray(stateSnap.data().agencies)
          ? stateSnap.data().agencies
          : [];
        const currentAgencyValue = sanitizeAgencyName(newAgency || currentUser?.agency || '');
        const sourceAgencies = currentAgencyValue
          ? [...agencies, currentAgencyValue]
          : agencies;
        const { sortedAgencies, normalizedMap, searchEntries } = buildAgencyLookup(sourceAgencies);

        if (cancelled) return;

        setAgencyOptions(sortedAgencies);
        setAgencyLookup(normalizedMap);
        setAgencySearchEntries(searchEntries);
      } catch (err) {
        console.error('Failed to load agencies for profile edit:', err);
        if (cancelled) return;
        setAgencyOptions([]);
        setAgencyLookup(new Map());
        setAgencySearchEntries([]);
      }
    };

    loadStateAgencies();
    return () => { cancelled = true; };
  }, [newState, newAgency, currentUser]);

	


useEffect(() => {
  if (showEditModal) {
    setPendingNewEmail(
      currentUser?.pendingEmailChange?.status === 'pending'
        ? currentUser?.pendingEmailChange?.newEmail || currentUser?.email || ''
        : currentUser?.email || ''
    );
    setEmailChangePassword('');
    const nextAgencyValue = sanitizeAgencyName(newAgency || currentUser?.agency || "");
    const normalizedAgency = normalizeAgencyName(nextAgencyValue);
    const matchedAgency = agencyLookup.get(normalizedAgency);

    if (matchedAgency) {
      setAgencySelection(matchedAgency);
      setCustomAgency('');
    } else {
      const nextAgencySelection = getEditableSelectionState(nextAgencyValue, agencyOptions);
      setAgencySelection(nextAgencySelection.selectedValue);
      setCustomAgency(nextAgencySelection.customValue);
    }

    const nextRankSelection = getEditableSelectionState(rank, rankOptions);
    setRankSelection(nextRankSelection.selectedValue);
    setCustomRank(nextRankSelection.customValue);

    const nextAssignmentSelection = getEditableSelectionState(assignment, assignmentOptions);
    setAssignmentSelection(nextAssignmentSelection.selectedValue);
    setCustomAssignment(nextAssignmentSelection.customValue);
  }
}, [showEditModal, currentUser, newAgency, rank, assignment, agencyLookup, agencyOptions, rankOptions, assignmentOptions]);

  useEffect(() => {
    if (!showEditModal || agencySelection !== 'Other') return;

    const exactMatch = agencyLookup.get(normalizeAgencyName(customAgency));
    if (!exactMatch) return;

    setAgencySelection(exactMatch);
    setNewAgency(exactMatch);
    setCustomAgency('');
  }, [showEditModal, agencySelection, customAgency, agencyLookup]);




	useEffect(() => {
  if (activeTab === 'subscription') {
    const fetchReferralsAndActivityLog = async () => {
      const authUser = getAuth().currentUser;
      if (!authUser) return;

      const userRef = doc(firestore, 'users', authUser.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) return;

      const data = snap.data();

      // 🔷 Referrals
      const rawReferrals = data.referrals || [];
      const usedReferrals = data.usedReferrals || [];

      const enriched = await Promise.all(
        rawReferrals.map(async (ref) => {
          const referredDoc = await getDoc(doc(firestore, 'users', ref.referredUserId));
          const referredData = referredDoc.exists() ? referredDoc.data() : {};

          return {
            referredUserId: ref.referredUserId,
            date: ref.date,
            firstInitialLastName: referredData.firstInitialLastName || 'Unknown',
            agency: referredData.agency || 'Unknown Agency'
          };
        })
      );

      setReferrals({
        all: enriched,
        used: usedReferrals
      });

      // 🔷 Activity Log
      const activity = (data.activityLog || []).sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setActivityLog(activity);
    };

    fetchReferralsAndActivityLog();
  }

if (activeTab === 'contact' && currentUser?.uid) {
    const fetchUserMessages = async () => {
  const q = query(
    collection(firestore, 'contactMessages'),
    where('userId', '==', currentUser.uid),
    orderBy('timestamp', 'desc')
  );
  const snap = await getDocs(q);

  const msgs = await Promise.all(
    snap.docs.map(async docSnap => {
      const data = docSnap.data();
      const repliesSnap = await getDocs(collection(docSnap.ref, 'replies'));
      const replies = repliesSnap.docs.map(replyDoc => ({
        id: replyDoc.id,
        ...replyDoc.data()
      }));
      return {
        id: docSnap.id,
        ...data,
        replies
      };
    })
  );

  setUserMessages(msgs);
};


    fetchUserMessages();
  }
}, [activeTab, currentUser?.uid]);




const handleAvatarChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const fileExt = file.name.split('.').pop();

  try {
    const auth = getAuth();
    const uid = auth.currentUser.uid;

    // ✅ explicitly target the firebasestorage.app bucket for avatars
    const altStorage = getStorage(app, 'blue-wire-e382b.firebasestorage.app');

    const storageRef = ref(altStorage, `avatars/${uid}/avatar.jpg`);
    console.log("📄 Uploading avatar:", file);
    console.log("👤 UID:", uid);
    console.log("🗂️ Path:", `avatars/${uid}/avatar.jpg`);

    await uploadBytes(storageRef, file);

    const downloadURL = await getDownloadURL(storageRef);
    console.log("✅ Download URL:", downloadURL);

    await updateDoc(doc(firestore, 'users', uid), {
      avatarUrl: downloadURL
    });

    setCurrentUser((prev) => ({
      ...prev,
      avatarUrl: downloadURL
    }));

    toast.success('✅ Avatar updated successfully.');
  } catch (err) {
    console.error("❌ Avatar upload error:", err);
    toast.error('❌ Failed to update avatar.');
  }
};



	const onCropComplete = (croppedArea, croppedAreaPixels) => {
  setCroppedAreaPixels(croppedAreaPixels);
};

const handleAvatarClick = () => {
  document.getElementById('avatar-file-input').click();
};

const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    setSelectedImage(URL.createObjectURL(file));
    setShowCropModal(true);
  }
};



	const handleSendContactMessage = async () => {
  try {
    await sendContactMessage(currentUser, contactTitle, contactMessage);
    toast.success('✅ Message sent successfully');
    setContactTitle('');
    setContactMessage('');
  } catch (err) {
    console.error(err);
    toast.error('❌ Failed to send message');
  }
};




	const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg');
    };
    image.onerror = (err) => reject(err);
  });
};



	const handleSaveCroppedAvatar = async () => {
  try {
    toast.info("Saving avatar…");
    const blob = await getCroppedImg(selectedImage, croppedAreaPixels);

    const auth = getAuth();
    const uid = auth.currentUser.uid;

    const altStorage = getStorage(app, 'blue-wire-e382b.firebasestorage.app');
    const storageRef = ref(altStorage, `avatars/${uid}/avatar.jpg`);

    await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(storageRef);

    await updateDoc(doc(firestore, 'users', uid), {
      avatarUrl: downloadURL
    });

    setCurrentUser((prev) => ({
      ...prev,
      avatarUrl: downloadURL
    }));

    toast.success('✅ Avatar updated successfully.');
    setShowCropModal(false);
  } catch (err) {
    console.error(err);
    toast.error('❌ Failed to save avatar.');
  }
};



const confirmNameChange = () => {
  const originalFullName = `${currentUser.firstName} ${currentUser.lastName}`;
  const newFullName = `${firstName} ${lastName}`;
  if (originalFullName !== newFullName) {
    setShowNameModal(true);
  }
};


/* Legacy profile save handler disabled in favor of
   handleSaveProfileChangesUnified().
	const handleSaveProfileChanges = async () => {
  const auth = getAuth();
  const uid = auth.currentUser.uid;
  const normalizedCustomAgency = normalizeAgencyName(customAgency);
  const existingAgency = agencyLookup.get(normalizedCustomAgency);
  const resolvedAgency = agencySelection === 'Other'
    ? existingAgency || sanitizeAgencyName(customAgency)
    : sanitizeAgencyName(agencySelection || newAgency);
  const resolvedRank = rankSelection === 'Other'
    ? String(customRank || '').trim()
    : String(rankSelection || rank || '').trim();
  const resolvedAssignment = assignmentSelection === 'Other'
    ? String(customAssignment || '').trim()
    : String(assignmentSelection || assignment || '').trim();
  // 🔒 Prevent saving empty required fields
const requiredFields = [
  { key: 'firstName', label: 'First name', value: firstName },
  { key: 'lastName', label: 'Last name', value: lastName },
  { key: 'agency', label: 'Agency', value: resolvedAgency },
  { key: 'rank', label: 'Rank', value: resolvedRank },
  { key: 'assignment', label: 'Assignment', value: resolvedAssignment },
  { key: 'state', label: 'State', value: newState },
];

for (const field of requiredFields) {
  if (!field.value || !field.value.trim()) {
    toast.error(`${field.label} cannot be empty.`);
    return; // ⛔ Stop save entirely
  }
}
  try {
    const userRef = doc(firestore, 'users', uid);

    const newFirstInitialLastName = `${firstName.charAt(0)}.${lastName}`;
    const newDisplayId = lastName;
    const newLastNameLower = lastName.toLowerCase();

   // 🔹 Build log entries for each changed field
const changedFields = {};
const logEntries = [];

const now = new Date().toISOString();

const compareAndLog = (fieldName, newValue, oldValue) => {
  if ((newValue || "") !== (oldValue || "")) {
    changedFields[fieldName] = newValue;
    logEntries.push({
      field: fieldName,
      oldValue: oldValue || "N/A",
      newValue: newValue || "N/A",
      changedAt: now,
      reason: "profile edit"
    });
  }
};

// Compare all editable fields (except aboutMe, which is not versioned)
compareAndLog("firstName", firstName, currentUser.firstName);
compareAndLog("middleInitial", middleInitial, currentUser.middleInitial);
compareAndLog("lastName", lastName, currentUser.lastName);
compareAndLog("rank", resolvedRank, currentUser.rank);
compareAndLog("assignment", resolvedAssignment, currentUser.assignment);
compareAndLog("agency", resolvedAgency, currentUser.agency);
compareAndLog("state", newState, currentUser.state);
if ((aboutMe || "") !== (currentUser.aboutMe || "")) {
  changedFields.aboutMe = aboutMe;
}

// Always update helper fields if the name changed
if (changedFields.firstName || changedFields.lastName) {
  changedFields.firstInitialLastName = `${firstName.charAt(0)}.${lastName}`;
  changedFields.displayId = lastName;
  changedFields.lastNameLower = lastName.toLowerCase();
}

// Final update payload
const updatePayload = {
  ...changedFields,
  changesLog: arrayUnion(...logEntries)
};

    console.log("Updating user document with:", updatePayload);

    // update name/rank/assignment first
   await updateDoc(userRef, {
  ...updatePayload,
  rank: resolvedRank,
  assignment: resolvedAssignment,
  agency: resolvedAgency,
  state: newState,
});

setCurrentUser(prev => ({ ...prev, ...changedFields }));

    setRank(resolvedRank);
    setAssignment(resolvedAssignment);
    setNewAgency(resolvedAgency);

    if (rankSelection === 'Other') {
      await upsertRegistrationOption('ranks', resolvedRank);
    }

    if (assignmentSelection === 'Other') {
      await upsertRegistrationOption('assignments', resolvedAssignment);
    }

    try {
      await upsertProfileSearchLookupsByState(
        newState,
        resolvedAgency,
        resolvedRank,
        resolvedAssignment
      );
    } catch (lookupErr) {
      console.warn('Failed to update profile search lookups:', lookupErr);
    }

    // 🔄 Re-fetch the updated user document
    const updatedSnap = await getDoc(userRef);
    const updatedData = updatedSnap.data();
    const enriched = { ...updatedData, uid };

    setCurrentUser(enriched);
    localStorage.setItem('currentUserProfile', JSON.stringify(enriched));
// ✅ Email change request
if (pendingNewEmail && pendingNewEmail !== currentUser.email) {
  await startEmailVerification(pendingNewEmail);
  setEmailVerificationSent(true);
  toast.info(`Verification email sent to ${pendingNewEmail}. Please verify to update your email.`);
  return; // don’t close modal until verified
}

// No email change → close modal normally
setShowEditModal(false);

  } catch (err) {
    console.error('Error saving profile changes:', err);
  }
};


/* Legacy email-change helpers disabled. The active flow uses
   verifyBeforeUpdateEmail() in handleSaveProfileChangesUnified.
const handleSendVerificationLink = async () => {
  if (!pendingNewEmail || pendingNewEmail === currentUser.email) {
    toast.error("Please enter a different email to update.");
    return;
  }

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      toast.error("You must be signed in to change your email.");
      return;
    }

    // 🔄 Step 1: Update email in Auth (marks unverified)
    await updateEmail(user, pendingNewEmail);

    // 🔄 Step 2: Send Firebase verification email
    await sendEmailVerification(user, {
      url: "https://www.blue-wire.org/confirm-new-email",
      handleCodeInApp: true,
    });

    // 🔄 Save state for later
    localStorage.setItem("pendingNewEmail", pendingNewEmail);
    setEmailVerificationSent(true);

    console.log("📤 Verification email sent to:", pendingNewEmail);
    toast.success(
      `Verification email sent to ${pendingNewEmail}. Please check inbox/spam.`
    );
  } catch (err) {
    console.error("❌ Failed to send verification email:", err);
    toast.error(`Failed to send verification email: ${err.message || err}`);
  }
};



const startEmailVerification = async (email) => {
  try {
    const functions = getFunctions();
    const newEmailVerification = httpsCallable(functions, 'newEmailVerification');

    await newEmailVerification({ newEmail: email });
    console.log('📤 Verification email sent via Resend (new):', email);

    localStorage.setItem('pendingNewEmail', email);
    setEmailVerificationSent(true);
    toast.success(`Verification email sent to ${email}. Please check inbox/spam.`);
  } catch (err) {
    console.error('❌ Failed to send verification email:', err);
    toast.error(`Failed to send verification email: ${err.message || err}`);
  }
};



*/
const savePassword = async () => {
  try {
    const auth = getAuth();
    await updatePassword(auth.currentUser, password);
    alert('Password updated successfully.');
    setPassword('');
    setIsEditingPassword(false);
  } catch (err) {
    console.error('Error updating password:', err);
  }
};


  const handleChangePassword = async () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user || !currentPasswordInput || !newPasswordInput) {
    toast.error('Please fill in all fields.');
    return;
  }

  const credential = EmailAuthProvider.credential(user.email, currentPasswordInput);

  try {
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPasswordInput);

    toast.success('Password updated successfully.');
    setShowPasswordForm(false);
    setCurrentPasswordInput('');
    setNewPasswordInput('');
  } catch (err) {
    console.error(err);
    toast.error('Current password is incorrect or something went wrong.');
  }
};



	const scheduleTimer = (msUntilToggle) => {
  if (window.dutyTimer) {
    clearTimeout(window.dutyTimer);
  }

  window.dutyTimer = setTimeout(async () => {
    const uid = getAuth().currentUser.uid;

    // Flip duty status
    const newStatus = currentUser.dutyStatus === 'on-duty' ? 'off-duty' : 'on-duty';

    await updateDoc(doc(firestore, 'users', uid), {
      dutyStatus: newStatus,
      'notificationPrefs.timer': null,
      'notificationPrefs.timerExpiresAt': null
    });

    setCurrentUser((prev) => ({
      ...prev,
      dutyStatus: newStatus,
      notificationPrefs: {
        ...prev.notificationPrefs,
        timer: null,
        timerExpiresAt: null
      }
    }));

    toast.info(`Duty status automatically switched to ${newStatus}.`);
  }, msUntilToggle);
};



	const startCountdown = (expiresAt) => {
  if (window.timerInterval) {
    clearInterval(window.timerInterval);
  }

  const updateRemaining = () => {
    const msRemaining = new Date(expiresAt).getTime() - Date.now();

    if (msRemaining <= 0) {
      clearInterval(window.timerInterval);
      setTimerRemaining('');
      return;
    }

    const totalSeconds = Math.floor(msRemaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    setTimerRemaining(
      `${hours}h ${minutes}m ${seconds}s remaining`
    );
  };

  updateRemaining(); // update immediately
  window.timerInterval = setInterval(updateRemaining, 1000);
};



	const handleDeleteMessage = async (messageId) => {
  if (!window.confirm('Are you sure you want to delete this message?')) return;

  try {
    await deleteDoc(doc(firestore, 'contactMessages', messageId)); // or whatever your collection is
    setUserMessages(prev => prev.filter(msg => msg.id !== messageId));
  } catch (error) {
    console.error('Error deleting message:', error);
    alert('Failed to delete message. Please try again.');
  }
};



const handleDeleteAccount = async () => {
  if (!window.confirm('⚠️ Are you sure you want to delete your account? This action cannot be undone.')) {
    return;
  }

  if (!deleteAccountPassword.trim()) {
    toast.error('Enter your current password to delete your account.');
    return;
  }

  const authInstance = getAuth();
  const user = authInstance.currentUser;
  if (!user) return;

  try {
    if (!user.email) {
      toast.error('Unable to verify your account email. Please sign in again.');
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, deleteAccountPassword);
      await reauthenticateWithCredential(user, credential);
    } catch (err) {
      console.error('Delete account reauthentication failed:', err);
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        toast.error('Incorrect password.');
        return;
      }
      if (err?.code === 'auth/requires-recent-login') {
        toast.error('Please sign in again to delete your account.');
        return;
      }
      toast.error(err?.message || 'Could not verify your password.');
      return;
    }

    try {
      const deleteOwnedPrivateNotesData = httpsCallable(getFunctions(), 'deleteOwnedPrivateNotesData');
      const result = await deleteOwnedPrivateNotesData();
      const deletedPrivateNotes = result?.data?.privateNotesDeleted || 0;
      if (deletedPrivateNotes > 0) {
        toast.info('Deleted private notes data.');
      }
    } catch (err) {
      console.error("Error deleting user's private notes data:", err);
      toast.error('Could not delete private notes data. Account deletion was stopped.');
      return;
    }

    await user.delete();

    localStorage.removeItem('currentUserProfile');
    setDeleteAccountPassword('');
    setShowDeleteAccountForm(false);
    toast.success('Your account has been deleted.');
    await authInstance.signOut();
    navigate('/login', { replace: true });
  } catch (err) {
    console.error('Delete account flow failed:', err);
    if (err?.code === 'auth/requires-recent-login') {
      toast.error('Please sign in again to delete your account.');
      return;
    }
    toast.error(err?.message || 'Failed to delete account. Please try again.');
  }
};


const syncCurrentUserCache = (profile) => {
  if (!profile) return;
  localStorage.setItem('currentUserProfile', JSON.stringify(profile));
};

const getResolvedProfileDraft = () => {
  const normalizedCustomAgency = normalizeAgencyName(customAgency);
  const existingAgency = agencyLookup.get(normalizedCustomAgency);
  const resolvedAgency = agencySelection === 'Other'
    ? existingAgency || sanitizeAgencyName(customAgency)
    : sanitizeAgencyName(agencySelection || newAgency);
  const resolvedRank = rankSelection === 'Other'
    ? String(customRank || '').trim()
    : String(rankSelection || rank || '').trim();
  const resolvedAssignment = assignmentSelection === 'Other'
    ? String(customAssignment || '').trim()
    : String(assignmentSelection || assignment || '').trim();
  const normalizedCurrentEmail = normalizeEmailValue(currentUser?.email);
  const normalizedRequestedEmail = normalizeEmailValue(pendingNewEmail);
  const hasPendingEmailChange = currentUser?.pendingEmailChange?.status === 'pending';
  const isEmailChangeRequested =
    !hasPendingEmailChange &&
    !!normalizedRequestedEmail &&
    normalizedRequestedEmail !== normalizedCurrentEmail;

  return {
    resolvedAgency,
    resolvedRank,
    resolvedAssignment,
    normalizedCurrentEmail,
    normalizedRequestedEmail,
    hasPendingEmailChange,
    isEmailChangeRequested,
  };
};

const handleSaveProfileChangesUnified = async (skipEmailConfirmationModal = false) => {
  const authInstance = getAuth();
  const user = authInstance.currentUser;
  const shouldSkipEmailConfirmationModal = skipEmailConfirmationModal === true;

  if (!user?.uid) {
    toast.error('You must be signed in to update your profile.');
    return;
  }

  const uid = user.uid;
  const userRef = doc(firestore, 'users', uid);
  const {
    resolvedAgency,
    resolvedRank,
    resolvedAssignment,
    normalizedRequestedEmail,
    hasPendingEmailChange,
    isEmailChangeRequested,
  } = getResolvedProfileDraft();
  const lastProfileEditDate = timestampToDate(currentUser?.lastProfileEditAt);
  const nextProfileEditDate = lastProfileEditDate
    ? new Date(lastProfileEditDate.getTime() + PROFILE_EDIT_COOLDOWN_MS)
    : null;
  const isProfileEditLocked =
    !!nextProfileEditDate && Date.now() < nextProfileEditDate.getTime();
  const nextProfileEditLabel = nextProfileEditDate
    ? nextProfileEditDate.toLocaleDateString()
    : '';

  const requiredFields = [
    { label: 'First name', value: firstName },
    { label: 'Last name', value: lastName },
    { label: 'Agency', value: resolvedAgency },
    { label: 'Rank', value: resolvedRank },
    { label: 'Assignment', value: resolvedAssignment },
    { label: 'State', value: newState },
  ];

  for (const field of requiredFields) {
    if (!String(field.value || '').trim()) {
      toast.error(`${field.label} cannot be empty.`);
      return;
    }
  }

  if (
    hasPendingEmailChange &&
    normalizedRequestedEmail &&
    normalizedRequestedEmail !== normalizeEmailValue(currentUser?.pendingEmailChange?.newEmail)
  ) {
    toast.error(`An email change is already pending for ${currentUser.pendingEmailChange.newEmail}.`);
    return;
  }

  if (isEmailChangeRequested && !/\S+@\S+\.\S+/.test(String(pendingNewEmail || '').trim())) {
    toast.error('Please enter a valid email address.');
    return;
  }

  if (isEmailChangeRequested) {
    const requestedEmailDomain = String(pendingNewEmail || '')
      .trim()
      .toLowerCase()
      .split('@')[1];

    if (!requestedEmailDomain) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (workEmailBlocklist.includes(requestedEmailDomain)) {
      toast.error('Please use work email');
      return;
    }
  }

  if (isEmailChangeRequested && !emailChangePassword.trim()) {
    toast.error('Enter your current password to change your email.');
    return;
  }

  if (isProfileEditLocked) {
    toast.error(
      `Profile edits are limited to once every 30 days. Next edit available on ${nextProfileEditLabel}.`
    );
    return;
  }

  if (isEmailChangeRequested && !shouldSkipEmailConfirmationModal) {
    setShowEmailChangeConfirmModal(true);
    return;
  }

  try {
    let pendingEmailChangeData = null;

    if (isEmailChangeRequested) {
      const credential = EmailAuthProvider.credential(user.email, emailChangePassword);

      try {
        await reauthenticateWithCredential(user, credential);
      } catch (err) {
        if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
          toast.error('Incorrect password.');
          return;
        }

        if (err?.code === 'auth/requires-recent-login') {
          toast.error('Please sign in again to change your email.');
          await authInstance.signOut();
          navigate('/login', { replace: true });
          return;
        }

        console.error('Email change reauthentication failed:', err);
        toast.error(err?.message || 'Could not verify your password.');
        return;
      }

      try {
        await verifyBeforeUpdateEmail(user, String(pendingNewEmail || '').trim(), {
          url: getEmailChangeContinueUrl(),
          handleCodeInApp: true,
        });
      } catch (err) {
        if (err?.code === 'auth/invalid-email') {
          toast.error('Please enter a valid email address.');
          return;
        }

        if (err?.code === 'auth/email-already-in-use') {
          toast.error('That email address is already in use.');
          return;
        }

        if (err?.code === 'auth/requires-recent-login') {
          toast.error('Please sign in again to change your email.');
          await authInstance.signOut();
          navigate('/login', { replace: true });
          return;
        }

        console.error('Email verification send failed:', err);
        toast.error(err?.message || 'Failed to send the email confirmation link.');
        return;
      }

      pendingEmailChangeData = {
        uid,
        oldEmail: currentUser.email || '',
        newEmail: String(pendingNewEmail || '').trim(),
        requestedAt: new Date().toISOString(),
        status: 'pending',
        requestId: buildRequestId(),
      };
    }

    const changedFields = {};
    const logEntries = [];
    const now = new Date().toISOString();

    const compareAndLog = (fieldName, newValue, oldValue) => {
      if ((newValue || '') !== (oldValue || '')) {
        changedFields[fieldName] = newValue;
        logEntries.push({
          field: fieldName,
          oldValue: oldValue || 'N/A',
          newValue: newValue || 'N/A',
          changedAt: now,
          reason: 'profile edit',
        });
      }
    };

    compareAndLog('firstName', firstName, currentUser.firstName);
    compareAndLog('middleInitial', middleInitial, currentUser.middleInitial);
    compareAndLog('lastName', lastName, currentUser.lastName);
    compareAndLog('rank', resolvedRank, currentUser.rank);
    compareAndLog('assignment', resolvedAssignment, currentUser.assignment);
    compareAndLog('agency', resolvedAgency, currentUser.agency);
    compareAndLog('state', newState, currentUser.state);

    if ((aboutMe || '') !== (currentUser.aboutMe || '')) {
      changedFields.aboutMe = aboutMe;
    }

    if (changedFields.firstName || changedFields.lastName) {
      changedFields.firstInitialLastName = `${firstName.charAt(0)}.${lastName}`;
      changedFields.displayId = lastName;
      changedFields.lastNameLower = lastName.toLowerCase();
    }

    const updatePayload = { ...changedFields };
    if (logEntries.length > 0) {
      updatePayload.changesLog = arrayUnion(...logEntries);
      updatePayload.lastProfileEditAt = serverTimestamp();
    }
    if (pendingEmailChangeData) {
      updatePayload.pendingEmailChange = pendingEmailChangeData;
    }

    if (Object.keys(updatePayload).length === 0) {
      setShowEditModal(false);
      return;
    }

    await updateDoc(userRef, updatePayload);

    if (
      rankSelection === 'Other' &&
      resolvedRank &&
      String(currentUser.rank || '').trim() !== resolvedRank
    ) {
      await upsertRegistrationOption('ranks', resolvedRank);
    }

    if (
      assignmentSelection === 'Other' &&
      resolvedAssignment &&
      String(currentUser.assignment || '').trim() !== resolvedAssignment
    ) {
      await upsertRegistrationOption('assignments', resolvedAssignment);
    }

    try {
      await upsertProfileSearchLookupsByState(
        newState,
        resolvedAgency,
        resolvedRank,
        resolvedAssignment
      );
    } catch (lookupErr) {
      console.warn('Failed to update profile search lookups:', lookupErr);
    }

    const updatedSnap = await getDoc(userRef);
    const updatedData = updatedSnap.data();
    const enriched = { ...updatedData, uid };

    setCurrentUser(enriched);
    syncCurrentUserCache(enriched);
    setRank(resolvedRank);
    setAssignment(resolvedAssignment);
    setNewAgency(resolvedAgency);
    setEmailChangePassword('');
    setShowEmailChangeConfirmModal(false);
    setShowEditModal(false);

    if (pendingEmailChangeData) {
      navigate('/verify-new-email-change');
      return;
    }

    toast.success('Profile updated successfully.');
  } catch (err) {
    console.error('Error saving profile changes:', err);
    toast.error(err?.message || 'Failed to save profile changes.');
  }
};

const {
  resolvedAgency,
  resolvedRank,
  resolvedAssignment,
  normalizedCurrentEmail,
  normalizedRequestedEmail,
  hasPendingEmailChange,
  isEmailChangeRequested,
} = getResolvedProfileDraft();

const lastProfileEditDate = timestampToDate(currentUser?.lastProfileEditAt);
const nextProfileEditDate = lastProfileEditDate
  ? new Date(lastProfileEditDate.getTime() + PROFILE_EDIT_COOLDOWN_MS)
  : null;
const isProfileEditLocked =
  !!nextProfileEditDate && Date.now() < nextProfileEditDate.getTime();
const nextProfileEditLabel = nextProfileEditDate
  ? nextProfileEditDate.toLocaleDateString()
  : '';
const profileEditLockMessage = isProfileEditLocked
  ? `Profile edits are limited to once every 30 days. Next edit available on ${nextProfileEditLabel}.`
  : '';

const shouldWarnAboutAgencyUpdate =
  isEmailChangeRequested &&
  normalizeAgencyName(resolvedAgency) === normalizeAgencyName(currentUser?.agency);

const isProfileValid =
  firstName?.trim() &&
  lastName?.trim() &&
  resolvedAgency &&
  resolvedRank &&
  resolvedAssignment &&
  newState?.trim() &&
  (
    hasPendingEmailChange ||
    !normalizedRequestedEmail ||
    normalizedRequestedEmail === normalizedCurrentEmail ||
    (
      /\S+@\S+\.\S+/.test(String(pendingNewEmail || '').trim()) &&
      !!emailChangePassword.trim()
    )
  );

const isProfileSaveDisabled = !isProfileValid || isProfileEditLocked;

const openProfileEditModal = () => {
  if (isProfileEditLocked) {
    toast.info(profileEditLockMessage);
    return;
  }
  setShowEditModal(true);
};

const customAgencyInput = sanitizeAgencyName(customAgency);
const normalizedCustomAgencyInput = normalizeAgencyName(customAgencyInput);
const customAgencySuggestions =
  agencySelection === 'Other' && normalizedCustomAgencyInput
    ? agencySearchEntries
        .filter(({ normalized }) => normalized.includes(normalizedCustomAgencyInput))
        .map(({ agencyName }) => agencyName)
    : [];



if (loadingUser) return <div>Loading user info...</div>;
if (!currentUser) return <div>User not found.</div>;




  return (
    <div className="my-account-container">
      <div className="page-info-header-block">
        <h1>My Account</h1>
        <div className="page-info-button-wrapper">
          <PageInfoButton title="About My Account">
            <p>Manage your identity, security, notifications, and support interactions from one place.</p>
            <ul className="page-info-list">
              <li><strong>Account tab:</strong> Update your profile photo, name, rank, agency, and password. Certain changes (like email) prompt verification before they take effect.</li>
              <li><strong>Notifications tab:</strong> Toggle duty status, adjust reminder timers, and review how the platform reaches you when critical alerts fire.
              Enable <u>Biometric Login</u> to sign in faster on this device using biometrics (Applies only to this device and does not change your account security elsewhere).</li>
              <li><strong>Contact Us:</strong> Send messages to the BlueWire team about billing, product questions, or technical support without leaving the app.</li>
            </ul>
          </PageInfoButton>
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'account' ? 'active' : ''}
          onClick={() => setActiveTab('account')}
        >
          Account
        </button>
        <button
          className={activeTab === 'notifications' ? 'active' : ''}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>

       <button
  className={activeTab === 'contact' ? 'active' : ''}
  onClick={() => setActiveTab('contact')}
>
  Contact Us
</button>


         {isPlatformAdmin && (
       <button
        className={activeTab === 'dashboard' ? 'active' : ''}
        onClick={() => setActiveTab('dashboard')}
       >
        Admin Dashboard
    </button>
  )}
</div>

<div className="tab-content">
      {activeTab === 'account' && (
  <>
  {currentUser?.pendingEmailChange?.status === 'pending' && (
    <div className="field" style={{ marginBottom: '16px', alignItems: 'flex-start' }}>
      <div>
        <strong>Email change pending:</strong> {currentUser.pendingEmailChange.newEmail}
      </div>
      <button
        type="button"
        className="btn-primary"
        onClick={() => navigate('/verify-new-email-change')}
      >
        Continue
      </button>
    </div>
  )}
  <ProfilePage
    currentUser={currentUser}
    setCurrentUser={setCurrentUser}
    handleAvatarClick={handleAvatarClick}
    handleFileChange={handleFileChange}
    handleSaveCroppedAvatar={handleSaveCroppedAvatar}
    handleChangePassword={handleChangePassword}
    handleDeleteAccount={handleDeleteAccount}
    deleteAccountFormRef={deleteAccountFormRef}
    passwordFormRef={passwordFormRef}
    showPasswordForm={showPasswordForm}
    setShowPasswordForm={setShowPasswordForm}
    currentPasswordInput={currentPasswordInput}
    setCurrentPasswordInput={setCurrentPasswordInput}
    newPasswordInput={newPasswordInput}
    setNewPasswordInput={setNewPasswordInput}
    showDeleteAccountForm={showDeleteAccountForm}
    setShowDeleteAccountForm={setShowDeleteAccountForm}
    deleteAccountPassword={deleteAccountPassword}
    setDeleteAccountPassword={setDeleteAccountPassword}
    firstName={firstName}
    setFirstName={setFirstName}
    middleInitial={middleInitial}
    setMiddleInitial={setMiddleInitial}
    lastName={lastName}
    setLastName={setLastName}
    rank={rank}
    setRank={setRank}
    assignment={assignment}
    setAssignment={setAssignment}
    aboutMe={aboutMe}
    setAboutMe={setAboutMe}
    newState={newState}
    setNewState={setNewState}
    newAgency={newAgency}
    setNewAgency={setNewAgency}
    showCropModal={showCropModal}
    setShowCropModal={setShowCropModal}
    selectedImage={selectedImage}
    setSelectedImage={setSelectedImage}
    crop={crop}
    setCrop={setCrop}
    zoom={zoom}
    setZoom={setZoom}
    croppedAreaPixels={croppedAreaPixels}
    setCroppedAreaPixels={setCroppedAreaPixels}
    onCropComplete={onCropComplete}
    showEditModal={showEditModal}
    setShowEditModal={setShowEditModal}
    handleSaveProfileChanges={handleSaveProfileChangesUnified}
    openProfileEditModal={openProfileEditModal}
    isProfileEditLocked={isProfileEditLocked}
    profileEditLockMessage={profileEditLockMessage}
    confirmNameChange={confirmNameChange}
    renderEditModal={false}
  />
  </>
)}


	{showEditModal && (
  <div
    className="modal-overlay"
  >
    <div className="modal-content">
      <h3>Edit Profile</h3>
    <div className="field">
  <label>State:</label>
  <select
    value={newState}
    onChange={(e) => {
      const selectedState = e.target.value;
      setNewState(selectedState);
      setAgencySelection('');
      setCustomAgency('');
      setNewAgency('');
    }}
  >
    {getStatesWithHomeFirst(currentUser?.state).map((stateName) => (
      <option key={stateName} value={stateName}>
        {stateName}
      </option>
    ))}
  </select>
</div>

    <div className="field">
      <label>Last Name:</label>
      <input
        type="text"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />
    </div>
    

    <div className="field">
      <label>Rank:</label>
      <div className="profile-edit-field-control">
        <select
          value={rankSelection}
          onChange={(e) => {
            setRankSelection(e.target.value);
            if (e.target.value !== 'Other') {
              setCustomRank('');
              setRank(e.target.value);
            }
          }}
        >
          <option value="">Select rank</option>
          {rankOptions.map((rankOption) => (
            <option key={rankOption} value={rankOption}>
              {rankOption}
            </option>
          ))}
          <option value="Other">Other</option>
        </select>
        {rankSelection === 'Other' && (
          <input
            type="text"
            value={customRank}
            onChange={(e) => setCustomRank(e.target.value)}
            placeholder="Enter rank"
          />
        )}
      </div>
    </div>

    <div className="field">
      <label>Assignment:</label>
      <div className="profile-edit-field-control">
        <select
          value={assignmentSelection}
          onChange={(e) => {
            setAssignmentSelection(e.target.value);
            if (e.target.value !== 'Other') {
              setCustomAssignment('');
              setAssignment(e.target.value);
            }
          }}
        >
          <option value="">Select assignment</option>
          {assignmentOptions.map((assignmentOption) => (
            <option key={assignmentOption} value={assignmentOption}>
              {assignmentOption}
            </option>
          ))}
          <option value="Other">Other</option>
        </select>
        {assignmentSelection === 'Other' && (
          <input
            type="text"
            value={customAssignment}
            onChange={(e) => setCustomAssignment(e.target.value)}
            placeholder="Enter assignment"
          />
        )}
      </div>
    </div>

    <div className="field">
      <label>Agency:</label>
      <div className="profile-edit-field-control">
        <select
          value={agencySelection}
          onChange={(e) => {
            setAgencySelection(e.target.value);
            if (e.target.value !== 'Other') {
              setCustomAgency('');
              setNewAgency(e.target.value);
            }
          }}
          disabled={!newState}
        >
          <option value="">Select agency</option>
          {agencyOptions.map((agencyOption) => (
            <option key={agencyOption} value={agencyOption}>
              {agencyOption}
            </option>
          ))}
          <option value="Other">Other</option>
        </select>
        {agencySelection === 'Other' && (
          <>
            <input
              type="text"
              value={customAgency}
              onChange={(e) => setCustomAgency(e.target.value)}
              placeholder="Enter agency"
            />
            {customAgencySuggestions.length > 0 && (
              <div className="profile-agency-suggestions">
                {customAgencySuggestions.map((agencyName) => (
                  <button
                    key={agencyName}
                    type="button"
                    className="profile-agency-suggestion"
                    onClick={() => {
                      setAgencySelection(agencyName);
                      setNewAgency(agencyName);
                      setCustomAgency('');
                    }}
                  >
                    {agencyName}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    <div className="field">
      <label>Email:</label>
      <input
        type="email"
        value={pendingNewEmail || ''}
        disabled={currentUser?.pendingEmailChange?.status === 'pending'}
        onChange={(e) => setPendingNewEmail(e.target.value)}
        placeholder={currentUser.email}
      />
    </div>

    {currentUser?.pendingEmailChange?.status === 'pending' && (
      <p style={{ color: '#8a6d3b', fontSize: '0.9em', marginTop: '8px' }}>
        Email change pending for <strong>{currentUser.pendingEmailChange.newEmail}</strong>.
      </p>
    )}

    {currentUser?.pendingEmailChange?.status !== 'pending' &&
      normalizeEmailValue(pendingNewEmail) !== normalizeEmailValue(currentUser?.email) && (
        <div className="field">
          <label>Current Password:</label>
          <input
            type="password"
            value={emailChangePassword}
            onChange={(e) => setEmailChangePassword(e.target.value)}
            placeholder="Enter current password"
          />
        </div>
      )}


<div className="modal-buttons">
  <button
    onClick={() => handleSaveProfileChangesUnified()}
    disabled={isProfileSaveDisabled}
    style={{
      opacity: isProfileSaveDisabled ? 0.5 : 1,
      cursor: isProfileSaveDisabled ? 'not-allowed' : 'pointer'
    }}
  >
    Save
  </button>
  <button onClick={() => {
    setShowEditModal(false);
    setEmailChangePassword('');
    setShowEmailChangeConfirmModal(false);
  }}>Cancel</button>
</div>
<p style={{ color: '#666', fontSize: '0.9em', marginTop: '8px', textAlign: 'center' }}>
  To prevent abuse, personal info edits are limited to once per month.
</p>
{profileEditLockMessage && (
  <p style={{ color: '#8a6d3b', fontSize: '0.9em', marginTop: '8px' }}>
    {profileEditLockMessage}
  </p>
)}
   </div>
</div>
)}

  {showEmailChangeConfirmModal && (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Confirm Email Change</h3>
        <div
          style={{
            width: '100%',
            maxWidth: '460px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            color: '#334155',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          <p>
            You are changing your work email to <strong>{pendingNewEmail}</strong>.
          </p>
          <p>Your new email must be confirmed to activate it.</p>
          <p>
            After the new email is confirmed,<strong> you will need to complete ID verification again</strong>.
          </p>
          {shouldWarnAboutAgencyUpdate && (
            <div
              style={{
                border: '1px solid #f59e0b',
                background: '#fffbeb',
                color: '#92400e',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              Your agency has not been updated. If this email belongs to a different agency,
              tap 'Go Back' and update that information before continuing.
            </div>
          )}
        </div>
        <div className="modal-buttons">
          <button
            type="button"
            onClick={() => handleSaveProfileChangesUnified(true)}
          >
            Send Confirmation Email
          </button>
          <button
            type="button"
            onClick={() => setShowEmailChangeConfirmModal(false)}
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  )}


	{showCropModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <h3>Edit Avatar</h3>
      <div className="crop-container">
        <Cropper
          image={selectedImage}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      <div className="controls">
        <input
          type="range"
          min={1}
          max={3}
          step={0.1}
          value={zoom}
          onChange={(e) => setZoom(e.target.value)}
        />
      </div>
      <div className="modal-buttons">
        <button onClick={() => setShowCropModal(false)}>Cancel</button>
        <button onClick={handleSaveCroppedAvatar}>Save</button>
      </div>
    </div>
  </div>
)}




        {activeTab === 'notifications' && (
          <div>
            <h2>Notification Preferences</h2>
            <div className="notification-preferences">
  <div className="field notification-field">
  <label className="notification-label" htmlFor="dutyStatusToggle">Duty Status</label>
  <label className="toggle-switch">
    <input
      id="dutyStatusToggle"
      type="checkbox"
      checked={currentUser.dutyStatus === 'on-duty'}
      onChange={async () => {
        const newStatus = currentUser.dutyStatus === 'on-duty' ? 'off-duty' : 'on-duty';
        await updateDoc(doc(firestore, 'users', getAuth().currentUser.uid), {
          dutyStatus: newStatus
        });
        setCurrentUser((prev) => ({ ...prev, dutyStatus: newStatus }));
      }}
    />
    <span className="slider"></span>
  </label>
</div>


  <div className="field notification-field">
  <div className="notification-help-text">
    Set a <strong>timer </strong>to auto-switch duty status (optional).
  </div>
  <select
    id="notificationTimerSelect"
    value={currentUser.notificationPrefs?.timer || ''}
   onChange={async (e) => {
  const value = e.target.value;
  const uid = getAuth().currentUser.uid;
  let expiresAt = null;

if (value === '10h' || value === '8h' || value === '12h') {
  let ms = 0;
  if (value === '8h') ms = 8 * 60 * 60 * 1000;
  if (value === '10h') ms = 10 * 60 * 60 * 1000; 
  if (value === '12h') ms = 12 * 60 * 60 * 1000;
  expiresAt = new Date(Date.now() + ms);
}


  await updateDoc(doc(firestore, 'users', uid), {
    'notificationPrefs.timer': value || null,
    'notificationPrefs.timerExpiresAt': expiresAt ? expiresAt.toISOString() : null
  });

  setCurrentUser((prev) => ({
    ...prev,
    notificationPrefs: {
      ...prev.notificationPrefs,
      timer: value || null,
      timerExpiresAt: expiresAt ? expiresAt.toISOString() : null
    }
  }));

  if (expiresAt) {
    const msUntilToggle = expiresAt.getTime() - Date.now();
    scheduleTimer(msUntilToggle);
    startCountdown(expiresAt);
  } else {
    setTimerRemaining('');
    if (window.timerInterval) {
      clearInterval(window.timerInterval);
    }
  }
}}

  >
    <option value="">None</option>
    <option value="8h">8 hours</option>
    <option value="10h">10 hours</option>
    <option value="12h">12 hours</option>
  </select>

	{timerRemaining && (
  <div className="timer-remaining">
    {timerRemaining}
  </div>
)}

</div>

 <div className="field notification-field">
  <label className="notification-label" htmlFor="messagingToggle">Messaging</label>
  <label className="toggle-switch">
    <input
      id="messagingToggle"
      type="checkbox"
      checked={currentUser.notificationPrefs?.messaging || false}
      onChange={async () => {
        const newVal = !currentUser.notificationPrefs?.messaging;
        await updateDoc(doc(firestore, 'users', getAuth().currentUser.uid), {
          'notificationPrefs.messaging': newVal
        });
        setCurrentUser((prev) => ({
          ...prev,
          notificationPrefs: {
            ...prev.notificationPrefs,
            messaging: newVal
          }
        }));
      }}
    />
    <span className="slider"></span>
  </label>
</div>


<div className="field notification-field">
  <label className="notification-label" htmlFor="boloToggle">BOLO</label>
  <label className="toggle-switch">
    <input
      id="boloToggle"
      type="checkbox"
      checked={currentUser.notificationPrefs?.bolo || false}
      onChange={async () => {
        const newVal = !currentUser.notificationPrefs?.bolo;
        await updateDoc(doc(firestore, 'users', getAuth().currentUser.uid), {
          'notificationPrefs.bolo': newVal
        });
        setCurrentUser((prev) => ({
          ...prev,
          notificationPrefs: {
            ...prev.notificationPrefs,
            bolo: newVal
          }
        }));
      }}
    />
    <span className="slider"></span>
  </label>
</div>

<div className="field notification-field">
  <label className="notification-label" htmlFor="biometricToggle">Biometric Login</label>
  <label className="toggle-switch">
    <input
      id="biometricToggle"
      type="checkbox"
      checked={biometricEnabled}
      onChange={async () => {
        if (!Capacitor.isNativePlatform()) {
          toast.info('Biometric login is only available in the mobile app.');
          return;
        }
        const newVal = !biometricEnabled;
        setBiometricEnabled(newVal);
        try {
          await Preferences.set({
            key: 'biometricEnabled',
            value: newVal ? 'true' : 'false',
          });
          if (newVal) {
            window.alert(
              'Please sign in once more with your email and password. After that, a "Login with biometrics" button will appear on the login screen.'
            );
          }
        } catch (err) {
          console.error('Failed to save biometric preference:', err);
          toast.error('Could not update biometric setting on this device.');
          setBiometricEnabled(!newVal);
        }
      }}
    />
    <span className="slider"></span>
  </label>
</div>
	     </div>
          </div>
        )}


	
  		{/* Contact Us */}

        {activeTab === 'contact' && (
  <div>
    <p>
      Share your comments, concerns, suggestions, and questions.
      We do our best to respond within 24 hours.
    </p>

    <div style={{ marginTop: '20px' }}>
     <input
  type="text"
  placeholder="Title (optional)"
  value={contactTitle}
  onChange={(e) => setContactTitle(e.target.value)}
  style={{
    width: '100%',
    marginBottom: '10px',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
  }}
/>

      <textarea
        placeholder="Your message"
        value={contactMessage}
        onChange={(e) => setContactMessage(e.target.value)}
        style={{ width: '100%', height: '200px', padding: '8px' }}
      />
      <button
        onClick={handleSendContactMessage}
        style={{
          marginTop: '10px',
          padding: '8px 16px',
          backgroundColor: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '4px'
        }}
      >
        Send Message
      </button>
    </div>

    <div style={{ marginTop: '30px' }}>
      <h3>Your Messages:</h3>
      {userMessages.length === 0 && <p>No messages submitted yet.</p>}
    {userMessages.map(msg => (
  <div
  key={msg.id}
  style={{
    position: 'relative',
    border: '1px solid #ccc',
    marginBottom: '10px',
    padding: '10px',
    borderRadius: '4px'
  }}
>

	 {/* Delete button */}
    <button
      onClick={() => handleDeleteMessage(msg.id)}
      style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        background: 'transparent',
        border: 'none',
        color: '#888',
        fontSize: '1.2em',
        cursor: 'pointer'
      }}
      title="Delete this message"
    >
      ×
    </button>

    <strong>{msg.title || '(No Title)'}</strong> — {msg.timestamp?.toDate ? new Date(msg.timestamp.toDate()).toLocaleString() : ''}
    <p>{msg.message}</p>
    <p style={{ fontSize: '0.9em', color: '#555' }}>Status: {msg.status}</p>

    {msg.replies?.length > 0 && (
     <div style={{ marginTop: '10px', fontStyle: 'italic', paddingLeft: '15px', borderLeft: '3px solid #007bff' }}>
  <strong style={{ display: 'block', marginBottom: '5px' }}>Reply:</strong>
        {msg.replies.map(reply => (
          <div key={reply.id} style={{ marginBottom: '8px' }}>
            <p>{reply.message}</p>
            <p style={{ fontSize: '0.8em', color: '#777' }}>
              {reply.timestamp?.toDate ? new 			 	 	Date(reply.timestamp.toDate()).toLocaleString() : ''}
            </p>
          </div>
        ))}
      </div>
    )}
  </div>
))}

    </div>
  </div>
)}




		{/* Admin Dashboard */}
	  
{activeTab === 'dashboard' && isPlatformAdmin && (
  <AdminDashboard />
)}



      </div>
	<ToastContainer />
    </div>
  );
};

export default MyAccount;
